/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, Suspense } from "react";
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ProfileContent from "./Settings/ProfileContent";
import PaymentProcessorContent from "./Settings/PaymentProcessorContent";
import NotificationsSection from "./Settings/NotificationsSection";
import ContributorsContent from "./Settings/ContributorsContent";
import AccountSettings from "./Settings/AccountSettings";
import ReferralContent from "./Settings/ReferralContent";
import EOReceiptsSection from "./Receipts/EOReceiptsSection";
import AppearanceSettings from "./Settings/AppearanceSettings";
import Subscription from "./Settings/Subscription";
import EOSocialMediaContent from "./Settings/SocialMediaContent";
import { getUserDetails } from "../../firebaseFunctions/getUserDetails/getUserDetails";
import { useAuth } from "../../context/AuthContext";
import ResponsiveTabs from "../ui/ResponsiveTabs";
import ProfileCompletionBanner from "../ui/ProfileCompletionBanner";

const LoadingSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-4 bg-grey-200 rounded w-1/4"></div>
    <div className="h-10 bg-grey-200 rounded w-full"></div>
    <div className="h-10 bg-grey-200 rounded w-full"></div>
    <div className="h-10 bg-grey-200 rounded w-3/4"></div>
  </div>
);

const SettingsContent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const urlTab = new URLSearchParams(location.search).get("tab");
  const initialTab = urlTab === "subscription" ? "Subscription"
    : urlTab === "payments" ? "Payment Processor"
    : urlTab === "notifications" ? "Notifications"
    : urlTab === "referrals" ? "Referrals"
    : urlTab === "social" ? "Social Media"
    : "Profile";
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [userDetails, setUserDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [popUpModalType, setPopUpModalType] = useState(null);
  const [userSignInMethod, setUserSignInMethod] = useState(null);
  const { user } = useAuth();

  const pageInfo = useMemo(() => {
    const info = {
      "Profile": { title: t('settings.title'), description: t('settings.settingsDescription') },
      "Account Settings": { title: t('settings.title'), description: t('settings.settingsDescription') },
      "Contributors": { title: t('settings.contributors'), description: t('settings.contributorsDescription') },
      "Notifications": { title: t('settings.notifications'), description: t('settings.notificationsDescription') },
      "Payment Processor": { title: t('settings.paymentProcessor'), description: t('settings.paymentDescription') },
      "Receipts": { title: t('settings.receipts'), description: t('settings.receiptsDescription') },
      "Referrals": { title: t('settings.referrals'), description: t('settings.referralsDescription') },
      "Appearance": { title: t('settings.appearance'), description: t('settings.appearanceDescription') },
      "Subscription": { title: "Subscription", description: "Manage your plan and billing" },
      "Social Media": { title: "Social Media", description: "Links shown on your public event pages" },
    };
    return info[selectedTab] || info["Profile"];
  }, [selectedTab, t]);

  const fetchUserDetails = async () => {
    if (user?.uid) {
      try {
        const details = await getUserDetails(user.uid);
        setUserDetails(details || {});
      } catch (error) {
        console.error("Error fetching user details:", error);
        setUserDetails({});
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [user?.uid]);

  useEffect(() => {
    if (user?.providerData) {
      const provider = user.providerData.find(p => 
        p.providerId === "google.com" || p.providerId === "password"
      );
      setUserSignInMethod(provider?.providerId === "google.com" ? "Google" : "Email");
    }
  }, [user]);

  const tabs = [
    { value: "Profile", label: t('settings.profile') },
    { value: "Account Settings", label: t('settings.accountSettings') },
    { value: "Contributors", label: t('settings.contributors') },
    { value: "Notifications", label: t('settings.notifications') },
    { value: "Payment Processor", label: t('settings.payments') },
    { value: "Receipts", label: t('settings.receipts') },
    { value: "Referrals", label: t('settings.referrals') },
    { value: "Appearance", label: t('settings.appearance') },
    { value: "Subscription", label: "Subscription" },
    { value: "Social Media", label: "Social Media" },
  ];

  const renderTabContent = () => {
    if (isLoading && selectedTab !== "Appearance") {
      return <LoadingSkeleton />;
    }

    switch (selectedTab) {
      case "Profile":
        return (
          <>
            <ProfileCompletionBanner
              fields={[
                { label: "Business Name", filled: !!userDetails?.businessName?.trim() },
                { label: "Contact Number", filled: !!userDetails?.contactNumber?.trim() },
                { label: "Email Address", filled: !!userDetails?.userEmail?.trim() },
                { label: "Business Description", filled: !!userDetails?.businessDescription?.trim() },
                { label: "Company Logo", filled: !!userDetails?.companyLogo },
              ]}
            />
            <ProfileContent
              userDetails={userDetails}
              reload={fetchUserDetails}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              popUpModalType={popUpModalType}
              setPopUpModalType={setPopUpModalType}
              userSignInMethod={userSignInMethod}
            />
          </>
        );
      case "Account Settings":
        return <AccountSettings userSignInMethod={userSignInMethod} />;
      case "Contributors":
        return <ContributorsContent userDetails={userDetails} />;
      case "Notifications":
        return <NotificationsSection />;
      case "Payment Processor":
        return <PaymentProcessorContent stripeConnected={userDetails?.stripeConnected} stripeAccountId={userDetails?.stripeAccountId} />;
      case "Receipts":
        return <EOReceiptsSection />;
      case "Referrals":
        return <ReferralContent userDetails={userDetails} />;
      case "Appearance":
        return <AppearanceSettings />;
      case "Subscription":
        return <Subscription />;
      case "Social Media":
        return <EOSocialMediaContent />;
      default:
        return <LoadingSkeleton />;
    }
  };

  return (
    <div className="w-full h-full bg-white">
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">{pageInfo.title}</h1>
          <p className="text-sm text-gray-500 mt-1">{pageInfo.description}</p>
        </div>

        <div className="mb-6">
          <ResponsiveTabs tabs={tabs} activeTab={selectedTab} onTabChange={setSelectedTab} />
        </div>

        <div className="min-h-[300px]">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsContent;
